import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useDrop } from 'react-dnd';

import styled from '@mint-lib/styled';
import type { Identifier } from 'dnd-core';

import { Box } from '@myn/mui';

import { Dimension } from '../../../types/campaignBuilder.js';
import {
  DEPENDENT_COLUMNS,
  DimensionsDragType,
  REQUIRED_COLUMNS,
} from '../EditModal.constants.js';
import {
  DraggableDimension,
  DimensionsGroup as TDimensionsGroup,
} from '../EditModal.types.js';
import DimensionsGroup from './DimensionsGroup/index.js';
import Header from './Header/index.js';

type Props = {
  groupedDimensions: TDimensionsGroup[];
  currentDimensions: Dimension[];
  setCurrentDimensions: Dispatch<SetStateAction<Dimension[]>>;
  onCustomSaveChange: (isActive: boolean) => void;
  className?: string;
  handleDimensionAddition: (dimension: Dimension, index: number) => void;
};

const AvailableColumnGroups = ({
  groupedDimensions,
  currentDimensions,
  setCurrentDimensions,
  onCustomSaveChange,
  className,
  handleDimensionAddition,
}: Props) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const ref = useRef<HTMLDivElement | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const [{ handlerId }, drop] = useDrop<
    DraggableDimension,
    void,
    {
      handlerId: Identifier | null;
      isOver: boolean;
    }
  >({
    accept: DimensionsDragType.DIMENSION_ROW,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    drop(draggableDimension) {
      const dimensionValue = draggableDimension.dimension?.value ?? '';

      if (REQUIRED_COLUMNS.includes(dimensionValue)) {
        return;
      }

      setCurrentDimensions((state) =>
        state.filter(
          (dimension) =>
            !(
              DEPENDENT_COLUMNS[dimensionValue]?.includes(dimension.value) ||
              dimension.value === dimensionValue
            ),
        ),
      );
    },
  });

  drop(ref);

  return (
    <Box
      className={className}
      ref={ref}
      data-handler-id={handlerId}
      sx={{ flexBasis: '50%', paddingBottom: '130px' }}
    >
      <StyledHeader onSearch={handleSearch} />
      {groupedDimensions.map((groupedDimension) => {
        return (
          <DimensionsGroup
            handleDimensionAddition={handleDimensionAddition}
            key={groupedDimension.label}
            group={groupedDimension}
            currentDimensions={currentDimensions}
            searchValue={searchValue}
            onCustomSaveChange={onCustomSaveChange}
          />
        );
      })}
    </Box>
  );
};

const StyledHeader = styled(Header)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export default memo(AvailableColumnGroups);
